import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";

const EQUIPMENT_RATES = {
  flatbed:  { cpm: 0.86, spotRate: 3.40, label: "Flatbed" },
  reefer:   { cpm: 0.83, spotRate: 3.20, label: "Reefer" },
  dryvan:   { cpm: 0.80, spotRate: 3.00, label: "Dry Van" },
};

const LEASE_EXPENSES = [
  { key: "fuel",       label: "Fuel (÷7 mpg × $6/gal)",   perMile: true,  perMileRate: 6 / 7 },
  { key: "truck",      label: "Truck Note",                  perMile: false, fixed: 700 },
  { key: "trailer",    label: "Trailer Lease",               perMile: false, fixed: 300 },
  { key: "cargo",      label: "Cargo Insurance",             perMile: false, fixed: 250 },
  { key: "physical",   label: "Physical Damage Insurance",   perMile: false, fixed: 250 },
  { key: "eld",        label: "ELD",                         perMile: false, fixed: 115 },
  { key: "maint",      label: "Maintenance Fund ($0.10/mi)", perMile: true,  perMileRate: 0.10 },
  { key: "fees",       label: "Direct Deposit + Roadside",   perMile: false, fixed: 65 },
];

function fmt(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function SettlementCalculator() {
  const [miles, setMiles] = useState(2500);
  const [equipment, setEquipment] = useState("flatbed");
  const [operation, setOperation] = useState("lease");

  const calc = useMemo(() => {
    const eq = EQUIPMENT_RATES[equipment];

    if (operation === "company") {
      const gross = miles * eq.cpm;
      return {
        type: "company",
        gross,
        net: gross,
        effectiveCpm: eq.cpm,
        lines: [],
      };
    }

    // Lease
    const revenueShare = 0.88;
    const gross = miles * eq.spotRate * revenueShare;
    const lines = LEASE_EXPENSES.map((exp) => {
      const amount = exp.perMile ? exp.perMileRate * miles : exp.fixed;
      return { label: exp.label, amount };
    });
    const totalExpenses = lines.reduce((s, l) => s + l.amount, 0);
    const net = gross - totalExpenses;
    const effectiveCpm = net / miles;

    return { type: "lease", gross, net, effectiveCpm, lines, totalExpenses };
  }, [miles, equipment, operation]);

  return (
    <section data-testid="settlement-calculator" className="bg-[#0A0A0A] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <span className="font-head text-xs uppercase tracking-[0.4em] text-primary">Interactive Tool</span>
          <h2 className="font-head text-3xl md:text-4xl font-bold uppercase tracking-[0.1em] text-white mt-3 leading-tight">
            Read Your Settlement Sheet
          </h2>
          <p className="mt-4 text-white/50 max-w-xl text-sm leading-relaxed">
            Adjust the inputs below and see every line of your weekly settlement — exactly how it looks on payday.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* ─── Left: Inputs ─── */}
          <div className="space-y-6">
            {/* Miles slider */}
            <div>
              <div className="flex justify-between items-baseline mb-3">
                <label className="font-head text-xs uppercase tracking-[0.3em] text-white/50">
                  Weekly Miles
                </label>
                <span className="font-head text-2xl font-bold text-white tabular-nums">
                  {miles.toLocaleString()}
                </span>
              </div>
              <input
                data-testid="settlement-miles-slider"
                type="range"
                min="500"
                max="4000"
                step="100"
                value={miles}
                onChange={(e) => setMiles(Number(e.target.value))}
                className="w-full h-1 bg-white/10 rounded-none appearance-none cursor-pointer
                           [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                           [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-none
                           [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0"
              />
              <div className="flex justify-between text-[10px] font-head uppercase tracking-[0.2em] text-white/20 mt-1">
                <span>500</span><span>4,000</span>
              </div>
            </div>

            {/* Equipment */}
            <div>
              <label className="font-head text-xs uppercase tracking-[0.3em] text-white/50 block mb-3">Equipment</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(EQUIPMENT_RATES).map(([key, val]) => (
                  <button
                    key={key}
                    data-testid={`settlement-eq-${key}`}
                    onClick={() => setEquipment(key)}
                    className={`py-3 font-head text-xs uppercase tracking-[0.15em] border transition-colors duration-150 ${
                      equipment === key
                        ? "bg-primary border-primary text-black"
                        : "bg-transparent border-white/10 text-white/60 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {val.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Operation */}
            <div>
              <label className="font-head text-xs uppercase tracking-[0.3em] text-white/50 block mb-3">Operation</label>
              <div className="grid grid-cols-2 gap-2">
                {[["company", "Company Driver"], ["lease", "Lease Purchase"]].map(([key, label]) => (
                  <button
                    key={key}
                    data-testid={`settlement-op-${key}`}
                    onClick={() => setOperation(key)}
                    className={`py-3 font-head text-xs uppercase tracking-[0.15em] border transition-colors duration-150 ${
                      operation === key
                        ? "bg-primary border-primary text-black"
                        : "bg-transparent border-white/10 text-white/60 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Context note */}
            <p className="text-[11px] text-white/25 leading-relaxed font-head uppercase tracking-[0.1em]">
              {operation === "company"
                ? "Company drivers have zero expenses — carrier covers fuel, truck, insurance, and ELD."
                : "Lease figures use 88% rev share, $4.00–$3.50/mi spot rates, and actual weekly expense model."}
            </p>
          </div>

          {/* ─── Right: Settlement Sheet ─── */}
          <div className="bg-white border border-border">
            {/* Sheet header */}
            <div className="bg-[#0A0A0A] border-b border-white/5 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/40">Hylas Companion</p>
                <p className="font-head text-xs uppercase tracking-[0.2em] text-white/80 mt-0.5">Driver Settlement — Weekly</p>
              </div>
              <span className={`font-head text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 ${
                operation === "company" ? "bg-green-400/10 text-green-400" : "bg-primary/10 text-primary"
              }`}>
                {operation === "company" ? "Company" : "Lease"}
              </span>
            </div>

            {/* Gross line */}
            <div className="px-6 py-4 border-b border-border/50 flex items-center justify-between">
              <div>
                <p className="font-head text-[10px] uppercase tracking-[0.25em] text-foreground/40">Gross Revenue</p>
                <p className="text-xs text-foreground/50 mt-0.5">
                  {operation === "company"
                    ? `${miles.toLocaleString()} mi × ${EQUIPMENT_RATES[equipment].cpm * 100}¢ CPM`
                    : `${miles.toLocaleString()} mi × $${EQUIPMENT_RATES[equipment].spotRate.toFixed(2)}/mi × 88%`}
                </p>
              </div>
              <span className="font-head text-lg font-bold text-[#0A0A0A] tabular-nums">{fmt(calc.gross)}</span>
            </div>

            {/* Expense lines */}
            {operation === "company" ? (
              <div className="px-6 py-8 text-center">
                <span className="font-head text-xs uppercase tracking-[0.25em] text-green-600">No Deductions</span>
                <p className="mt-2 text-sm text-foreground/50">Carrier covers all expenses. Your gross equals your net.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {calc.lines.map((line) => (
                  <div key={line.label} className="px-6 py-3 flex items-center justify-between">
                    <span className="text-xs text-foreground/60">{line.label}</span>
                    <span className="font-head text-sm text-red-500 tabular-nums">−{fmt(line.amount)}</span>
                  </div>
                ))}
                {/* Total expenses */}
                <div className="px-6 py-3 flex items-center justify-between bg-[#FAFAFA]">
                  <span className="font-head text-[11px] uppercase tracking-[0.2em] text-foreground/50">Total Expenses</span>
                  <span className="font-head text-sm font-bold text-red-600 tabular-nums">−{fmt(calc.totalExpenses)}</span>
                </div>
              </div>
            )}

            {/* Net pay footer */}
            <div className="bg-[#0A0A0A] px-6 py-5 flex items-center justify-between">
              <div>
                <p className="font-head text-[10px] uppercase tracking-[0.3em] text-white/40">Estimated Net Pay</p>
                <p className="font-head text-[11px] uppercase tracking-[0.15em] text-white/30 mt-0.5">
                  Effective {calc.effectiveCpm > 0 ? `${(calc.effectiveCpm * 100).toFixed(1)}¢` : "—"} CPM
                </p>
              </div>
              <span
                data-testid="settlement-net-pay"
                className="font-head text-3xl font-bold text-primary tabular-nums"
              >
                {fmt(Math.max(0, calc.net))}
              </span>
            </div>

            {/* Disclaimer + CTA */}
            <div className="px-6 py-4 border-t border-border/30 flex items-center justify-between gap-4">
              <p className="text-[10px] text-foreground/30 leading-relaxed">
                Estimated figures. Actual settlement varies by load, lane, and fuel price.
              </p>
              <Link
                to="/apply"
                data-testid="settlement-apply-cta"
                className="shrink-0 inline-flex items-center gap-1.5 font-head text-[11px] uppercase tracking-[0.15em] bg-[#0A0A0A] text-white px-4 py-2.5 hover:bg-primary hover:text-black transition-colors duration-200"
              >
                Apply <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
